import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useStore } from '../stores';

/** Replaces template strings with form data and parses HTML tags */
export const useFormatVariantJson = () => {
  const {
    formStore: { formData, currentModule },
  } = useStore();
  const { t, i18n } = useTranslation('common');
  const language = i18n.language as 'en' | 'es';

  const _parseHTML = (text?: string): React.ReactNode => {
    if (!text) return '';
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;

    // Regex to match h1 tags with their content (including nested tags like strong)
    const h1Regex = /<h1>(.*?)<\/h1>/g;
    let match;

    while ((match = h1Regex.exec(text)) !== null) {
      const [fullMatch, content] = match;
      const matchIndex = match.index;

      // Add any text before this h1 tag as plain string
      if (matchIndex > lastIndex) {
        const textBefore = text.substring(lastIndex, matchIndex);
        if (textBefore) {
          elements.push(textBefore);
        }
      }

      // Create Typography component for h1 (18px) - strong tags inside will be preserved
      elements.push(
        <Typography
          key={`h1-${matchIndex}`}
          component="div"
          sx={{
            fontSize: 18,
            lineHeight: 1.2,
            mb: 0.5,
            fontWeight: 500,
          }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );

      lastIndex = matchIndex + fullMatch.length;
    }

    // Add any remaining text after the last h1 tag as plain string
    if (lastIndex < text.length) {
      const textAfter = text.substring(lastIndex);
      if (textAfter) {
        elements.push(textAfter);
      }
    }

    // If no h1 tags were found, return the whole text as-is
    if (elements.length === 0) {
      return text;
    }

    return elements;
  };

  const _injectTemplateStrings = (template?: string): string => {
    if (!template) return '';
    return template.replace(/{{(.*?)}}/g, (_, key) => {
      if (key === 'name') {
        return formData.firstName || '';
      } else if (key === 'help_needed') {
        const goalOptions = t('helpOptions', {
          returnObjects: true,
        }) as { [key: string]: string };
        return goalOptions[formData.debtGoal]?.toLowerCase() || '';
      }
      return '';
    });
  };

  const parseText = (text?: string) => _parseHTML(_injectTemplateStrings(text));

  const getFormattedText = (node: 'title' | 'description') => {
    const headers = currentModule?.headers?.[language];
    if (headers) {
      const textNode = headers[node];
      if (textNode) return parseText(textNode);
    }
    return '';
  };

  return { parseText, getFormattedText };
};
